import type { ReactNode } from 'react'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'

const COLOR_OPEN_TAG_REGEX = /^\[color=(#[0-9A-Fa-f]{3}(?:[0-9A-Fa-f]{3})?(?:[0-9A-Fa-f]{2})?)\]/
const COLOR_CLOSE_TAG = '[/color]'
const LUX_PLUS_LOGO_TAG = '[luxplus]'

const INLINE_MARKUP_DELIMITERS: ReadonlyArray<{
  delimiter: string
  render: (children: ReactNode, key: string) => ReactNode
}> = [
  {
    delimiter: '***',
    render: (children, key) => (
      <strong key={key}>
        <em>{children}</em>
      </strong>
    ),
  },
  {
    delimiter: '**',
    render: (children, key) => <strong key={key}>{children}</strong>,
  },
  {
    delimiter: '__',
    render: (children, key) => <u key={key}>{children}</u>,
  },
  {
    delimiter: '*',
    render: (children, key) => <em key={key}>{children}</em>,
  },
]

const splitWithLineBreaks = (value: string, keyPrefix: string): Array<ReactNode> => {
  const lines = value.split('\n')
  return lines.flatMap((line, index) => {
    const nodes: Array<ReactNode> = [line]
    if (index < lines.length - 1) {
      nodes.push(<br key={`${keyPrefix}-br-${index.toString()}`} />)
    }
    return nodes
  })
}

const renderInlineMarkup = (value: string, keyPrefix: string): Array<ReactNode> => {
  const nodes: Array<ReactNode> = []
  let cursor = 0
  let segmentIndex = 0
  let textBuffer = ''

  const flushTextBuffer = () => {
    if (textBuffer.length === 0) {
      return
    }
    nodes.push(...splitWithLineBreaks(textBuffer, `${keyPrefix}-text-${segmentIndex.toString()}`))
    segmentIndex += 1
    textBuffer = ''
  }

  while (cursor < value.length) {
    if (value.startsWith(LUX_PLUS_LOGO_TAG, cursor)) {
      flushTextBuffer()
      nodes.push(
        <LuxPlusLogo
          key={`${keyPrefix}-luxplus-${segmentIndex.toString()}`}
          colour="inherit"
          height={10}
          inline
        />,
      )
      segmentIndex += 1
      cursor += LUX_PLUS_LOGO_TAG.length
      continue
    }

    const colorTagMatch = value.slice(cursor).match(COLOR_OPEN_TAG_REGEX)
    if (colorTagMatch) {
      const [openTag = '', colorHex = ''] = colorTagMatch
      const colorCloseIndex = value.indexOf(COLOR_CLOSE_TAG, cursor + openTag.length)
      if (colorCloseIndex >= 0) {
        flushTextBuffer()
        const inner = value.slice(cursor + openTag.length, colorCloseIndex)
        nodes.push(
          <span
            key={`${keyPrefix}-color-${segmentIndex.toString()}`}
            style={{ color: colorHex }}
          >
            {renderInlineMarkup(inner, `${keyPrefix}-color-inner-${segmentIndex.toString()}`)}
          </span>,
        )
        segmentIndex += 1
        cursor = colorCloseIndex + COLOR_CLOSE_TAG.length
        continue
      }
    }

    const inlineDelimiterConfig = INLINE_MARKUP_DELIMITERS.find(({ delimiter }) => value.startsWith(delimiter, cursor))
    if (inlineDelimiterConfig) {
      const openDelimiter = inlineDelimiterConfig.delimiter
      const closeIndex = value.indexOf(openDelimiter, cursor + openDelimiter.length)
      if (closeIndex >= 0) {
        flushTextBuffer()
        const inner = value.slice(cursor + openDelimiter.length, closeIndex)
        const renderedInner = renderInlineMarkup(inner, `${keyPrefix}-inline-inner-${segmentIndex.toString()}`)
        nodes.push(inlineDelimiterConfig.render(renderedInner, `${keyPrefix}-inline-wrap-${segmentIndex.toString()}`))
        segmentIndex += 1
        cursor = closeIndex + openDelimiter.length
        continue
      }
    }

    textBuffer += value[cursor]
    cursor += 1
  }

  flushTextBuffer()
  return nodes
}

export const renderHeaderBannerRichText = (value: string): Array<ReactNode> => {
  const nodes = renderInlineMarkup(value, 'root')
  return nodes.length > 0 ? nodes : ['']
}
